import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, FormSection, Button, LoadingSpinner, useToastActions } from '@airclou/ui'
import {
  CreditCard,
  TrendingUp,
  CheckCircle2,
  // Database, Zap -- used by the hidden Usage Overview section
  AlertTriangle,
  CalendarSync,
  Download,
  Mail,
  Receipt,
  BarChart3,
  DollarSign,
  Clock,
} from 'lucide-react'
import { getMyPlan, isPaying } from '../../../services/plans'
import { useAuth } from '../../../contexts/AuthContext'
import { canSeeBilling } from '../../../navigation'
import { listInvoices, openBillingPortal } from '../../../services/billing'
import { CancelSubscriptionModal } from '../components/CancelSubscriptionModal'
import { InvoiceStatusBadge, formatMoney } from '../components/invoiceDisplay'
import { usePageRestored } from '../../../hooks/usePageRestored'
import type { MyPlan } from '../../../services/plans'
import type { InvoiceSummary } from '../../../services/billing'
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateInTz } from '@airclou/utils'

export function OverviewTab() {
  const timezone = useTimezone()
  const navigate = useNavigate()
  const { t } = useTranslation('dashboard')

  const [myPlan, setMyPlan] = useState<MyPlan | null>(null)
  const [isLoadingPlan, setIsLoadingPlan] = useState(true)
  const [planError, setPlanError] = useState<string | null>(null)
  const [showCancelModal, setShowCancelModal] = useState(false)

  // The Service suite — the only one sold today
  // (docs/plans/free-plan-and-reverse-trial-plan.md §3.1).
  const service = myPlan?.suites.find((b) => b.suite === 'service') ?? myPlan?.suites[0] ?? null
  const subscription = service?.subscription ?? null
  // Paid for right now, read from the subscription — not the tier's source,
  // which a trial still sets while a cheaper subscription is already live.
  const paying = isPaying(service)
  const onGrant = !paying && service?.grant?.live === true

  // Only a live subscription has something to cancel, and only one that has
  // not already cancelled — read from Stripe, since the subscription stays
  // live until Stripe's own event ends it.
  const canCancel =
    paying &&
    !service?.billingProviderUnavailable &&
    subscription?.status !== 'canceled' &&
    !subscription?.cancelAtPeriodEnd

  // Subscription state is read live from Stripe, so "we could not ask" is a
  // real answer. Say it, rather than hiding the button and letting a paying
  // customer conclude they have no subscription.
  const billingUnreadable = service?.billingProviderUnavailable === true

  useEffect(() => {
    loadMyPlan()
  }, [])

  async function loadMyPlan() {
    setIsLoadingPlan(true)
    setPlanError(null)
    try {
      const data = await getMyPlan()
      setMyPlan(data)
    } catch (err: any) {
      console.error('Failed to load plan:', err)
      setPlanError(err.message || t('billingPlans.loadFailed'))
    } finally {
      setIsLoadingPlan(false)
    }
  }

  // Invoices are Stripe's, read live and never sampled. The overview shows the
  // latest three and what was paid over the last three months.
  const [invoices, setInvoices] = useState<InvoiceSummary[]>([])
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(true)
  const [invoicesError, setInvoicesError] = useState<string | null>(null)
  const [openingPortal, setOpeningPortal] = useState(false)
  // Null until the invoices answer. On a failed read it stays null and the
  // portal button stays usable: the server then says what is wrong.
  const [hasBillingAccount, setHasBillingAccount] = useState<boolean | null>(null)
  const toast = useToastActions()

  // The invoice routes refuse roles outside billing, and the sidebar hides
  // Billing from them, but the URL still opens. For a role known to be
  // refused, the invoice cards are left out rather than shown as an error.
  // An unknown role still asks: the server decides.
  const { user } = useAuth()
  const invoicesHidden = user?.orgRole !== undefined && !canSeeBilling(user?.orgRole ?? undefined)

  useEffect(() => {
    if (!invoicesHidden) void loadInvoices()
  }, [invoicesHidden])

  async function loadInvoices() {
    setIsLoadingInvoices(true)
    setInvoicesError(null)
    try {
      const view = await listInvoices()
      setInvoices(view.invoices)
      setHasBillingAccount(view.hasBillingAccount)
    } catch (err) {
      setInvoicesError(err instanceof Error && err.message ? err.message : t('billing.invoicesLoadFailed'))
    } finally {
      setIsLoadingInvoices(false)
    }
  }

  const recentInvoices = invoices.slice(0, 3)
  const threeMonthsAgo = Date.now() - 90 * 24 * 60 * 60 * 1000
  const recentWindow = invoices.filter((inv) => new Date(inv.createdAt).getTime() >= threeMonthsAgo)
  const paidRecently = recentWindow.filter((inv) => inv.status === 'paid')
  const unpaidRecently = recentWindow.filter((inv) => inv.status === 'open' || inv.status === 'uncollectible')
  const spentCents = paidRecently.reduce((sum, inv) => sum + inv.amountPaidCents, 0)
  const spentCurrency = paidRecently[0]?.currency ?? invoices[0]?.currency ?? 'usd'

  // The billing email is edited in Stripe's portal, which brings the browser
  // back here. On success the page is left, so the button stays disabled.
  async function openPortalForBillingEmail() {
    setOpeningPortal(true)
    try {
      await openBillingPortal({ returnTab: 'overview' })
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : t('billing.portalFailed'))
      setOpeningPortal(false)
    }
  }

  // Back from the portal instead of its return link: re-arm the button and
  // re-read the invoices.
  usePageRestored(() => {
    setOpeningPortal(false)
    if (!invoicesHidden) void loadInvoices()
  })

  // Usage Overview is hidden (see the commented-out section below).
  /*
  const [usage] = useState({
    storage: { used: 2.4, limit: 10, unit: 'GB', percentage: 24 },
    apiCalls: { used: 8750, limit: 10000, percentage: 88 },
    seats: { used: 18, limit: 25, percentage: 72 },
  })
  */

  const formatDate = (dateString: string) => formatDateInTz(dateString, timezone)

  /*
  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-500'
    if (percentage >= 75) return 'bg-yellow-500'
    // eslint-disable-next-line no-restricted-syntax -- usage-meter ramp (red/yellow/blue), not a brand surface
    return 'bg-blue-500'
  }
  */

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className={`grid grid-cols-1 gap-6 ${invoicesHidden ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
        {/* Plan Summary Card */}
        <Card>
          <CardContent className="pt-6">
            {isLoadingPlan ? (
              <div className="flex items-center justify-center py-4">
                <LoadingSpinner />
              </div>
            ) : service && onGrant ? (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('billing.currentPlan')}</p>
                    <h3 className="text-2xl font-bold text-foreground mt-1">
                      {t('billingPlans.planName', { suite: service.suiteName, tier: t('billingPlans.tier_pro') })}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t(service.grant?.kind === 'extension' ? 'billingPlans.extensionThenFree' : 'billingPlans.freeTrialThenFree')}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900 rounded-full flex items-center justify-center">
                    <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                  </div>
                </div>
                <div className="mt-4 flex items-center text-sm">
                  <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400 mr-1" />
                  <span className="text-amber-600 dark:text-amber-400 font-medium">
                    {t('billingPlans.daysRemaining', { count: service.grant?.daysRemaining })}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 w-full"
                  onClick={() => navigate('/billing/subscription')}
                >
                  {t('billingPlans.seePlans')}
                </Button>
              </>
            ) : service ? (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('billing.currentPlan')}</p>
                    <h3 className="text-2xl font-bold text-foreground mt-1">
                      {t('billingPlans.planName', {
                        suite: service.suiteName,
                        tier: t(`billingPlans.tier_${paying ? subscription!.tier : service.tier}`),
                      })}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {paying
                        ? subscription?.seats != null
                          ? t('billingPlans.seats', { count: subscription.seats })
                          : t('billingPlans.paid')
                        : t('billingPlans.everyFeatureNoAi')}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-primary/15 dark:bg-primary/25 rounded-full flex items-center justify-center">
                    <CalendarSync className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <div className="mt-4 flex items-center text-sm">
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 mr-1" />
                  <span className="text-green-600 dark:text-green-400 font-medium">{t('billing.active')}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 w-full"
                  onClick={() => navigate('/billing/subscription')}
                >
                  {paying ? t('billing.changePlan') : t('billingPlans.seePlans')}
                </Button>
              </>
            ) : planError ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('billing.currentPlan')}</p>
                  <p className="text-sm text-red-600 dark:text-red-400 mt-2">{planError}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => loadMyPlan()}>
                  {t('common.retry', 'Retry')}
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('billing.nextBilling')}</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">
                  {paying && subscription?.periodEndsAt ? formatDate(subscription.periodEndsAt) : '--'}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {!paying
                    ? t('billingPlans.nothingToPay')
                    : billingUnreadable
                      ? t('billingPlans.couldntReachProvider')
                      : subscription?.cancelAtPeriodEnd || subscription?.status === 'canceled'
                        ? t('billingPlans.planEndsOn')
                        : t('billingPlans.nextRenewal')}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm text-muted-foreground">
              <Clock className="h-4 w-4 mr-1" />
              {/* Renewal is only known when Stripe could be read: unknown is
                  said as unknown, never as "off". */}
              <span>
                {!paying
                  ? t('billingPlans.noCharges')
                  : billingUnreadable
                    ? t('billingPlans.renewalUnknown')
                    : subscription?.cancelAtPeriodEnd || subscription?.status === 'canceled'
                      ? t('billingPlans.renewalOff')
                      : t('billing.autoRenewalEnabled')}
              </span>
            </div>
          </CardContent>
        </Card>

        {!invoicesHidden && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('billing.totalSpent3mo')}</p>
                  <h3 className="text-2xl font-bold text-foreground mt-1">
                    {isLoadingInvoices ? (
                      <span className="inline-block h-7 w-24 animate-pulse rounded bg-muted align-middle" aria-hidden="true" />
                    ) : invoicesError ? (
                      '--'
                    ) : (
                      formatMoney(spentCents, spentCurrency)
                    )}
                  </h3>
                  {!isLoadingInvoices && !invoicesError && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('billing.invoicesCount', { count: paidRecently.length })}
                    </p>
                  )}
                </div>
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                  <Receipt className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
              {/* Read from the invoices: "all paid" is only said when there was something to pay. */}
              {!isLoadingInvoices && !invoicesError && (
                unpaidRecently.length > 0 ? (
                  <div className="mt-4 flex items-center text-sm text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="h-4 w-4 mr-1" />
                    <span>{t('billing.unpaidCount', { count: unpaidRecently.length })}</span>
                  </div>
                ) : recentWindow.length > 0 ? (
                  <div className="mt-4 flex items-center text-sm text-green-600 dark:text-green-400">
                    <TrendingUp className="h-4 w-4 mr-1" />
                    <span>{t('billing.allPaid')}</span>
                  </div>
                ) : (
                  <div className="mt-4 flex items-center text-sm text-muted-foreground">
                    <Clock className="h-4 w-4 mr-1" />
                    <span>{t('billingPlans.noCharges')}</span>
                  </div>
                )
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Usage Overview -- Hidden 2026-10-05: hardcoded sample figures, not this organization's usage.
          Nothing meters storage or API calls yet; restore once real usage counts
          exist (docs/plans/ai-allowance-and-seat-caps-plan.md).
      <FormSection title={t('billing.currentUsage')} contentClassName="space-y-6">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium">{t('billing.storage')}</span>
            </div>
            <span className="text-sm text-muted-foreground">
              {usage.storage.used} / {usage.storage.limit} {usage.storage.unit}
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${getUsageColor(usage.storage.percentage)}`}
              style={{ width: `${usage.storage.percentage}%` }}
            />
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium">{t('billing.apiCalls')}</span>
            </div>
            <span className="text-sm text-muted-foreground">
              {usage.apiCalls.used.toLocaleString()} / {usage.apiCalls.limit.toLocaleString()}
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${getUsageColor(usage.apiCalls.percentage)}`}
              style={{ width: `${usage.apiCalls.percentage}%` }}
            />
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium">{t('billing.teamSeats')}</span>
            </div>
            <span className="text-sm text-muted-foreground">
              {usage.seats.used} / {usage.seats.limit} {t('billing.seats')}
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${getUsageColor(usage.seats.percentage)}`}
              style={{ width: `${usage.seats.percentage}%` }}
            />
          </div>
        </div>
      </FormSection>
      */}

      {/* Recent Invoices */}
      {!invoicesHidden && (
        <FormSection
          title={t('billing.recentInvoices')}
          actions={
            <Button variant="outline" size="sm" onClick={() => navigate('/billing/invoices')}>
              {t('billing.viewAll')}
            </Button>
          }
          contentClassName="space-y-3"
        >
          {isLoadingInvoices && invoices.length === 0 ? (
            <div className="flex items-center justify-center py-4">
              <LoadingSpinner />
            </div>
          ) : invoicesError ? (
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">{invoicesError}</p>
              <Button variant="outline" size="sm" onClick={() => void loadInvoices()}>
                {t('common.retry')}
              </Button>
            </div>
          ) : recentInvoices.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('billing.noInvoicesYet')}</p>
          ) : (
            recentInvoices.map((invoice) => (
              <div key={invoice.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                    <Receipt className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{invoice.number ?? invoice.id}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(invoice.createdAt)}</p>
                  </div>
                </div>
                <div className="text-right space-y-1">
                  <p className="font-medium">{formatMoney(invoice.totalCents, invoice.currency)}</p>
                  <InvoiceStatusBadge status={invoice.status} />
                </div>
              </div>
            ))
          )}
        </FormSection>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormSection title={t('billing.manageSubscription')} contentClassName="space-y-3">
          <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/billing/subscription')}>
            <CalendarSync className="h-4 w-4 mr-2" />
            {t('billing.changePlan')}
          </Button>
          <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/billing/payment-methods')}>
            <CreditCard className="h-4 w-4 mr-2" />
            {t('billing.updatePaymentMethod')}
          </Button>
          {/* Only a paying organization has subscription details to miss. */}
          {paying && billingUnreadable && (
            <p className="text-sm text-muted-foreground">{t('billingPlans.stripeDown')}</p>
          )}
          {/* Only offered to someone who actually has a subscription — a
              trial or an expired account has nothing to cancel, and the
              backend answers NO_SUBSCRIPTION for both. */}
          {canCancel && (
            <Button
              variant="outline"
              className="w-full justify-start text-red-600 hover:text-red-700 dark:hover:text-red-400 border-red-200 hover:border-red-300"
              onClick={() => setShowCancelModal(true)}
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              {t('billing.cancelSubscription')}
            </Button>
          )}
        </FormSection>

        <FormSection title={t('billing.billingResources')} contentClassName="space-y-3">
          <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/billing/invoices')}>
            <Download className="h-4 w-4 mr-2" />
            {t('billing.downloadInvoices')}
          </Button>
          <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/billing/reports')}>
            <BarChart3 className="h-4 w-4 mr-2" />
            {t('billing.viewReports')}
          </Button>
          {!invoicesHidden && (
            <Button
              variant="outline"
              className="w-full justify-start"
              // Nothing to edit until Stripe knows the organization, as on the Payment methods tab.
              disabled={openingPortal || isLoadingInvoices || hasBillingAccount === false}
              onClick={() => void openPortalForBillingEmail()}
            >
              <Mail className="h-4 w-4 mr-2" />
              {openingPortal ? t('billing.openingPortal') : t('billing.updateBillingEmail')}
            </Button>
          )}
        </FormSection>
      </div>

      {showCancelModal && (
        <CancelSubscriptionModal
          suite={service?.suite ?? 'service'}
          periodEndsAt={subscription?.periodEndsAt}
          onClose={() => setShowCancelModal(false)}
          onCancelled={() => {
            setShowCancelModal(false)
            void loadMyPlan()
          }}
        />
      )}
    </div>
  )
}
